// Tuya Testing — the production bench, ported from the Flutter app's
// `TuyaTesting/tuya_testing_page.dart`.
//
// WHAT IT IS FOR, because the name does not say it: this is the screen a
// technician opens on the assembly line. It lists the devices that spoke to the
// broker MOST RECENTLY — regardless of owner, room or location — so a board that
// was flashed and powered on thirty seconds ago is at the top of the list with
// no account, no mapping and no setup of any kind. From there its relays can be
// clicked, its DeviceType can be corrected, and its Wi-Fi can be handed over.
//
// TWO ENDPOINTS, both verified live 2026-08-03:
//
//   GetListofDevicesForTuyaTesting   the DeviceType catalog filtered to
//                                    AllowTestingTuya = 1 — 8 rows, which is
//                                    what the per-card dropdown offers.
//   GetallDeviceStatusForTuyaTesting { LimitCount, DeviceID } — the most
//                                    recently seen devices, newest first, with
//                                    their last stored Status string.
//                                    DeviceID 0 means "any type".
//
// ⚠️ THE STATUS IN THAT REPLY IS A SNAPSHOT, not a subscription. It is whatever
// the server last wrote, which may be minutes old. The screen subscribes to each
// `<uno>/client` on top of it — the REST value is only the starting picture.

import { post, updateDeviceInstalled } from '../api/api';
import { parseServerTime } from './firmwareInfo';
import { ONLINE_WINDOW_MS } from './deviceStatus';

// ── The three card shapes ──────────────────────────────────────────────────
//
// Straight from the Dart branch order. A DeviceType outside all three draws
// nothing at all in Flutter; here it falls through to `null` and the screen says
// so rather than rendering an empty box.

/** Comma-list status: `[uno]0,0,0,0#src` — one value per relay. */
export const TUYA_MULTI_TYPES = [49, 56];

/** Dash status: `[uno]1-0#src` — a single relay. */
export const TUYA_SINGLE_TYPES = [60, 6, 21, 22, 33, 40, 42, 43, 45, 55, 59];

/** Dash status carrying a SPEED, not a boolean. */
export const TUYA_FAN_TYPES = [53];

export function tuyaKind(deviceId) {
  const id = Number(deviceId);
  if (TUYA_MULTI_TYPES.includes(id)) return 'multi';
  if (TUYA_FAN_TYPES.includes(id)) return 'fan';
  if (TUYA_SINGLE_TYPES.includes(id)) return 'single';
  return null;
}

// ── Fan ladder ─────────────────────────────────────────────────────────────
//
// ⚠️ NOT the same ladder as core/devices/statusFormat. That one is the customer
// fan screen's (10, 30, 40 … 100). The Dart bench slider is `max: 100,
// divisions: 5`, i.e. 0/20/40/60/80/100, and displays `value ~/ 20` as the speed
// number — so the two screens genuinely disagree about what step 1 is. This file
// keeps the BENCH's ladder, because the point of the bench is to reproduce what
// the technician sees, and changing it here would silently change the command
// that gets sent.
export const TUYA_FAN_OFF = 10;
export const TUYA_FAN_STEP = 20;
export const TUYA_FAN_MAX = 100;

/** The number printed next to the dial: 0–5. */
export const tuyaFanStep = (value) => Math.trunc(Number(value || 0) / TUYA_FAN_STEP);

/** Slider position for a reported value — the OFF sentinel 10 sits at zero. */
export const tuyaFanSliderValue = (value) => {
  const v = Number(value);
  if (!Number.isFinite(v) || v === TUYA_FAN_OFF) return 0;
  return v;
};

/** What goes on the wire for a slider position — zero becomes the sentinel. */
export const tuyaFanCommandValue = (sliderValue) =>
  (Number(sliderValue) === 0 ? TUYA_FAN_OFF : Number(sliderValue));

// ── Parsing a stored / live status ─────────────────────────────────────────

/** Everything between `]` and `#`, tolerant of both being absent. */
function statusBody(raw) {
  let s = String(raw ?? '');
  // The firmware occasionally emits a stray replacement character mid-frame;
  // the Dart strips it before splitting and so must we, or a channel parses as
  // NaN and the tile renders off.
  s = s.replace(/�/g, '');
  const close = s.indexOf(']');
  if (s.includes('[') && close !== -1) s = s.slice(close + 1);
  const hash = s.indexOf('#');
  return (hash === -1 ? s : s.slice(0, hash)).trim();
}

/**
 * A device's channel values, as strings.
 *
 * Strings, not numbers, deliberately: a fan speed and a relay state live in the
 * same array and only the caller knows which is which. `''` for an unparseable
 * frame keeps the tile count stable rather than collapsing the card.
 */
export function parseTuyaStatus(raw, deviceId) {
  const kind = tuyaKind(deviceId);
  const body = statusBody(raw);

  if (kind === 'fan') {
    // A fan that has never reported reads as OFF rather than as an empty card —
    // same default as the Dart, which seeds "10".
    if (!body || body.includes(',')) return [String(TUYA_FAN_OFF)];
    return [body.replace('1-', '')];
  }

  if (kind === 'single') return [body.replace('1-', '')];

  if (!body) return [];
  return body.split(',').map((v) => v.trim());
}

/**
 * The Status seed written when a device's type is corrected.
 *
 * The row has to carry a status the new type can parse, or the card would draw
 * from the OLD shape until the device next reports — four tiles for a device
 * that now has one. Values are the Dart's exactly.
 */
export function seedStatusFor(deviceId) {
  const id = Number(deviceId);
  if (id === 49) return '0,0,0,0#';
  if (id === 53) return '1-10#';
  if (id === 55 || id === 60) return '1-0#';
  if (id === 56) return '0,0#';
  return '';
}

// ── "When did we last hear from it" ────────────────────────────────────────
//
// The whole point of the bench's liveness column: watch a device DROP, and know
// when it dropped, rather than discovering later that it is gone.
//
// ── Why three minutes is the right unit here ───────────────────────────────
// The firmware heartbeats on a fixed timer, and stops the moment it loses the
// server — `OTAOneSwitchwithButton.ino`:
//
//     if (ServerConnected && (millis() > LastIpUpdatedTime + 180000)) { … }
//
// so silence past one beat is real evidence, not jitter. The `ServerConnected`
// guard is what makes it a usable offline signal at all.
//
// ⚠️ THIS LADDER IS DELIBERATELY TIGHTER THAN THE REST OF THE APP, and it must
// not be copied out of this file. `deviceStatus.ONLINE_WINDOW_MS` is TEN
// minutes, because a remote control that cries wolf is worse than one that is
// briefly stale — measured on live hardware, healthy devices were 5.6, 6.9 and
// 6.9 minutes stale. On a bench the trade runs the other way round: the
// technician is standing in front of the board and WANTS to see it go quiet.
// So `late` starts at one missed beat and is called `late`, not `offline` —
// only past the app's own ten-minute window is a device called gone.
export const TUYA_HEARTBEAT_MS = 3 * 60 * 1000;

/** One missed beat, plus 30 s of slack for the round trip. */
export const TUYA_LIVE_MS = TUYA_HEARTBEAT_MS + 30 * 1000;

/** Past this it is offline by the app's own definition, not just late. */
export const TUYA_GONE_MS = ONLINE_WINDOW_MS;

export const SEEN_LIVE = 'live';
export const SEEN_LATE = 'late';
export const SEEN_GONE = 'gone';
export const SEEN_NEVER = 'never';

/** Age in ms -> one of the four states. `null` age means never heard from. */
export function seenStateOf(ageMs) {
  if (ageMs == null || !Number.isFinite(ageMs)) return SEEN_NEVER;
  if (ageMs <= TUYA_LIVE_MS) return SEEN_LIVE;
  if (ageMs <= TUYA_GONE_MS) return SEEN_LATE;
  return SEEN_GONE;
}

/** `9s` / `4m` / `2h 07m` — short enough for a chip, exact enough to act on. */
export function shortAge(ageMs) {
  if (ageMs == null || !Number.isFinite(ageMs)) return '—';
  const s = Math.max(0, Math.round(ageMs / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

/** The "seen within" filter. `null` shows everything, including never-seen. */
export const TUYA_SEEN_WINDOWS = [
  { ms: null, label: 'Any' },
  { ms: 1 * 60 * 1000, label: '1 min' },
  { ms: TUYA_HEARTBEAT_MS, label: '3 min' },
  { ms: 5 * 60 * 1000, label: '5 min' },
  { ms: 10 * 60 * 1000, label: '10 min' },
  { ms: 20 * 60 * 1000, label: '20 min' },
  { ms: 60 * 60 * 1000, label: '1 hour' },
];

/**
 * When the server last stamped this device, as real epoch ms.
 *
 * ⚠️ GOES THROUGH parseServerTime, ALWAYS. The backend writes IST wall-clock and
 * the JSON serialiser appends a `Z`, so a raw `Date.parse` reads every row 5½
 * hours in the FUTURE — which makes every age negative and every device look
 * like it reported in the future. See deviceStatus.SERVER_TZ_OFFSET_MS.
 */
export const lastSeenFromRow = (row) => parseServerTime(row?.LastUpdatedDateTime);

// ── Live traffic ───────────────────────────────────────────────────────────
//
// Port of the Dart `processTxtReceive`. The bench sees far more than status
// frames — boot banners, OTA progress, ping replies — and the whole point is
// that all of it is shown. Only frames that actually carry channel values are
// allowed to move the tiles.

// Substrings that mark a message as DIAGNOSTIC: print it, never parse it.
// Lower-cased comparison, so the list is lower-case.
const INFO_MARKERS = [
  'restart', 'ws,', 's05,', 'restartwifimodule', 'unauthorised', '?????',
  'devicetype', 'mqttping', 'update', 'updating', 'http', 'spot',
];

const SERVER_URL = 'http://smartifyindia.co.in';

const unoOf = (s) => {
  const open = s.indexOf('[');
  const close = s.indexOf(']');
  return open !== -1 && close > open ? s.slice(open + 1, close) : null;
};

/**
 * Classify one `<uno>/client` payload.
 *
 * @returns {{uno: string, kind: 'info'|'status'|'started', text: string,
 *            values?: string[]}|null}
 *   `null` for anything that belongs to no device on screen.
 *
 * `started` means the device has just booted — the Dart re-reads the whole list
 * on it, because a freshly flashed board announcing itself is exactly the row
 * the technician is waiting for.
 */
export function classifyTuyaMessage(payload) {
  const raw = String(payload ?? '');
  if (!raw) return null;
  const lower = raw.toLowerCase();
  const uno = unoOf(raw);
  if (!uno) return null;

  if (lower.includes('started')) return { uno, kind: 'started', text: raw };

  if (INFO_MARKERS.some((m) => lower.includes(m))) {
    // The OTA URL is masked the way the Dart masks it: the line is long, the
    // host is the same every time, and what matters is the filename after it.
    return { uno, kind: 'info', text: raw.split(SERVER_URL).join('***') };
  }

  const close = raw.indexOf(']');
  if (close === -1) return null;
  const body = statusBody(raw);
  if (!body) return { uno, kind: 'info', text: raw };

  const values = raw.slice(close + 1).includes('1-')
    ? [body.replace('1-', '')]
    : body.split(',').map((v) => v.trim());

  return { uno, kind: 'status', text: raw, values };
}

// ── REST ───────────────────────────────────────────────────────────────────

/** The DeviceType catalog the bench may assign — AllowTestingTuya = 1. */
export const getTuyaDeviceTypes = () => post('GetListofDevicesForTuyaTesting', {});

/**
 * The most recently seen devices.
 * @param {number} limitCount how many rows
 * @param {number} deviceId   0 for every type
 */
export const getTuyaDeviceStatuses = (limitCount = 10, deviceId = 0) =>
  post('GetallDeviceStatusForTuyaTesting', {
    LimitCount: Number(limitCount) || 10,
    DeviceID: Number(deviceId) || 0,
  });

/**
 * Correct a device's type on the install row.
 *
 * Writes DeviceID, the seed Status, and the two fields the Dart sends alongside
 * them — UartPins 'RXTX' and OTABinFileId 9. Both are constants on the bench:
 * every board it sees is the current generation.
 *
 * The caller must follow this with a `restartwifimodule`, or the device keeps
 * reporting in its old shape until it is power-cycled by hand.
 */
export const setTuyaDeviceType = (uniqueNumber, deviceId) =>
  updateDeviceInstalled(uniqueNumber, {
    DeviceID: Number(deviceId),
    UartPins: 'RXTX',
    OTABinFileId: 9,
    Status: `[${uniqueNumber}]${seedStatusFor(deviceId)}`,
  });
